import React, { Component } from 'react';
import { Input, Button, Card, Menu } from '@nackle/paper';
import { Plus, Minus, ChevronDown, ChevronRight, Menu2 } from '@nackle/icons';
import { Filter } from '@nackle/icons/dist/react-es/ultimate';
import { sortBy } from 'lodash';
import { Cabinet, CabinetEntry, Room } from 'shared/interfaces';
import { RoomActions } from './interfaces';
import './cabinet-toggler-style.less';

export interface CabinetSelectorProps {
    readonly noResultsText?: string;
    readonly noCabinetsText?: string;
    readonly noAvailableCabinets?: string;
    readonly selectedHeaderText?: string;
    readonly availableHeaderText?: string;
    readonly filterPlaceholderText?: string;
    readonly room: Room;
    readonly cabinetOptions: Array<Cabinet>;
    readonly roomActions: RoomActions;
}

export interface CabinetSelectorState {
    query: string;
    expandSelected: boolean;
    expandAvailable: boolean;
    totalAdded: number;
}

/**
 * This CabinetSelector component creates two lists of cabinets: a list of selected 
 * cabinets and a list of available cabinets. As the names imply, the selected cabinets
 * are the cabinets that have been, well, selected. The available list is made up of 
 * cabinets that have yet to be selected (this means selected cabinets won't show up 
 * in the available list). 
 * 
 * Both cabinet lists also use the same filtering (handled by an input field positioned
 * above the lists and list headers). In situations where there are many cabinets that
 * can potentially be selected, or are selected, the ability to filter may make it easier
 * if searching for a specific cabinet. 
 */
export default class CabinetSelector extends Component<CabinetSelectorProps, CabinetSelectorState>{

    private static readonly CLASS_GROUP_CARD: string = 'cabinet-list-card';
    private static readonly CLASS_MENU: string = 'menu-selectable-cabinets';
    private static readonly CLASS_LIST_ITEM: string = 'selectable-cabinet-list-item';
    private static readonly CLASS_LIST_ITEM_AVAILABLE: string = 'selectable-cabinet-list-items-available';

    private static readonly ID_LIST_HEADER_SELECTED: string = 'cabinet-selector-section-header-selected';
    private static readonly ID_LIST_HEADER_AVAILABLE: string = 'cabinet-selector-section-header-available';
    private static readonly ID_LIST_SELECTED: string = 'cabinet-selector-list-selected';
    private static readonly ID_LIST_AVAILABLE: string = 'cabinet-selector-list-available';

    private static readonly ICON_ADD_CABINET = <Plus/>;
    private static readonly ICON_REMOVE_CABINET = <Minus/>;

    public draggedCabinet: CabinetEntry | undefined = undefined;
    public draggedIndex: number | null = null;
    
    /**
     * Default props to be used in case the props provided to this component
     * are missing.
     */
    static defaultProps = {
        minHeight: undefined,
        maxHeight: undefined,
        noResultsText: 'No results',
        noCabinetsText: 'There are no cabinets in the room',
        selectedHeaderText: 'Selected',
        availableHeaderText: 'Available',
        noAvailableCabinets: 'There are no additional cabinets',
        filterPlaceholderText: 'Filter cabinets...',
    };
    
    /**
     * The initial state of the component.
     */
    public state: CabinetSelectorState = {
        query: '',
        expandSelected: true,
        expandAvailable: false,
        totalAdded: 0,
    };
    

    constructor(props: CabinetSelectorProps) {
        super(props);

        // Request updated cabinets from the api.
        props.roomActions.fetchLatestCabinets();
    }

    /**
     * Method called when a cabinet has started being dragged.
     */
    public onDragStart = (event, index) => {
        this.draggedCabinet = this.props.room.cabinets[ index ];
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/html', event.target.parentNode);
        event.dataTransfer.setDragImage(event.target.parentNode, 20, 20);
    };

    /**
     * Called whenever a dragged cabinet is being dragged over other cabinets. 
     * The reordering takes place here before the dragging has actually stopped. 
     */
    public onDragOver = (index) => {
        if (this.draggedIndex === index) {
            return;
        }
        this.draggedIndex = index;

        const { draggedCabinet } = this;
        if (!draggedCabinet) {
            return;
        }

        const cabinets = this.props.room.cabinets.filter(cabinet => cabinet.cabinetId !== draggedCabinet.cabinetId);
        cabinets.splice(index, 0, draggedCabinet);
        this.props.roomActions.setReorderedCabinets(cabinets);
    };

    /**
     * Called when the dragging of a cabinet is released. Resets the appropriate
     * variables.
     */
    public onDragEnd = () => {
        this.draggedIndex = null;
    };

    public render() {
        const { 
            room: {
                cabinets
            },
            cabinetOptions,
            filterPlaceholderText,
            selectedHeaderText, 
            availableHeaderText,
        } = this.props;
        const { 
            query,
            expandSelected,
            expandAvailable,
        } = this.state;

        // Compose lists and counts
        const selectedCabinets = cabinets;
        const selectedCabinetsFiltered = selectedCabinets.filter((entry: CabinetEntry) => this.acceptedByQuery(query, entry));
        const availableCabinets = cabinetOptions.filter((cabinet: Cabinet) => (cabinets.map(entry => entry.cabinetId).indexOf(cabinet.cabinetId) === -1));
        const availableCabinetsFiltered = sortBy(availableCabinets.filter((entry: CabinetEntry) => this.acceptedByQuery(query, entry)), 'name'); // Also alphabetized

        // Figure out section labels
        const selectedLabel = `${selectedHeaderText} (${(query !== '')
            ? `${selectedCabinetsFiltered.length} of ${selectedCabinets.length} shown`
            : `${selectedCabinets.length}`})`;

        const availableLabel = `${availableHeaderText} (${(query !== '')
            ? `${availableCabinetsFiltered.length} of ${availableCabinets.length} shown`
            : `${availableCabinets.length}`})`;

        return (
            <>
                <Card 
                    className={ CabinetSelector.CLASS_GROUP_CARD }
                >
                    <Input.Group >
                        <Input
                            onChange={this.handleQueryChange}
                            placeholder={filterPlaceholderText}
                            prefix={<Filter/>}
                        />
                    </Input.Group>

                    {/* SELECTED SECTION - Header label if not empty */}
                    { (selectedLabel !== '') && 
                        this.renderSectionHeader(
                            CabinetSelector.ID_LIST_HEADER_SELECTED,
                            selectedLabel,
                            expandSelected, 
                            () => this.setState({ expandSelected: !this.state.expandSelected })
                        )
                    }
                    {/* SELECTED SECTION - Render the selected cabinets if section is expanded */}
                    { (expandSelected) &&
                        <Menu 
                            id={ CabinetSelector.ID_LIST_SELECTED }
                            className={CabinetSelector.CLASS_MENU}
                        >
                            {this.renderSelectionSection(selectedCabinetsFiltered)}
                        </Menu>
                    }

                    {/* AVAILABLE SECTION - Header label if not empty */}
                    { (availableLabel !== '') && 
                        this.renderSectionHeader(
                            CabinetSelector.ID_LIST_HEADER_AVAILABLE,
                            availableLabel,
                            expandAvailable, 
                            () => this.setState({ expandAvailable: !this.state.expandAvailable })
                        )
                    }
                    {/* AVAILABLE SECTION - Render the available cabinets if section is expanded */}
                    { (expandAvailable) &&
                        <Menu
                            id={ CabinetSelector.ID_LIST_AVAILABLE }
                            className={CabinetSelector.CLASS_MENU}
                        >
                            {this.renderAvailableSection(availableCabinetsFiltered)}
                        </Menu>
                    }
                </Card>
            </>
        );
    }

    /**
     * Renders the header of a section.
     * @param label - The text to display on the header.
     * @param expanded - Whether the header should be formatted as collapsed or not.
     * @param onClick - The callback when this header is clicked.
     */
    private renderSectionHeader = (id: string, label: string, expanded: boolean, onClick: () => void) => {
        return (
            <Input.Group style={{ paddingTop: 'inherit' }}
            >
                <Button
                    block
                    id={ id }
                    icon={ expanded ? <ChevronDown/> : <ChevronRight/> }
                    onClick={ onClick }
                    size="small"
                >
                    { label }
                </Button>
            </Input.Group>
        );
    };

    /**
     * Returns a menu with list items of the filtered cabinets.
     * @param listItems - The list of cabinet entries to show in the selection list.
     */
    private renderSelectionSection = (listItems: CabinetEntry[]) => {
        // Grab relevant props and state data
        const { noCabinetsText, noResultsText } = this.props;
        const { query } = this.state;
        const filtering = (query !== '');

        // If there are selected cabinets to show, return a list of those cabinets
        if (listItems.length !== 0) {
            return (
                <>
                    { (listItems.length > 0) &&
                        listItems.map((entry: CabinetEntry, index: number) => {
                            return (
                                <li
                                    className='cabinet-arcade-list-item'
                                    key={ `room-cabinet-list-item-${entry.cabinetId}` }
                                    id={ `arcadeRoomCabinet-${entry.cabinetId }` }
                                    onDragOver={ !filtering 
                                        ? () => this.onDragOver(index) 
                                        : undefined 
                                    }
                                >
                                    <Input.Group 
                                        className={ CabinetSelector.CLASS_LIST_ITEM }
                                        // fill={ true }
                                        size="large"
                                        style={{display: 'flex', alignItems: 'center' }}
                                    >
                                        <Button
                                            className="fixed"
                                            type="primary"
                                            onClick={ () => this.handleCabinetRemove(entry) }
                                            icon={ CabinetSelector.ICON_REMOVE_CABINET }
                                            style={{marginRight: 10}}
                                        />
                                        
                                        <div 
                                            className={ 'menu-item intent-primary' } 
                                            draggable={ !filtering }
                                            onDragStart={ !filtering ? () => this.onDragStart(event, index) : undefined }
                                            onDragEnd={ !filtering ? this.onDragEnd : undefined } 
                                            style={{display: 'flex'}}
                                        >
                                            <div className={'text-overflow-ellipsis classes-fill'}>
                                                {entry.name}
                                            </div>
                                            { !filtering &&
                                               <Menu2 className='list-item-menu'/>
                                            }
                                        </div>
                                    </Input.Group>
                                </li>
                            );
                        })
                    }
                </>
            );
        } 
        // If no cabinets are being shown, show the no results or no cabinets message
        else {
            return ( 
                <li style={{ paddingTop: 10}}>
                    <span>
                        {filtering ? noResultsText : noCabinetsText}
                    </span>
                </li>
            );
        }
    };

    /**
     * Returns a menu with list items of the filtered cabinets. Formated as the list of
     * cabinets available to be added to the room.
     * @param listItems - The list of cabinet entries to show in the available list.
     */
    private renderAvailableSection = (listItems: Cabinet[]) => {
        // Grab relevant props and state data
        const { noAvailableCabinets, noResultsText } = this.props;
        const { query } = this.state;
        const filtering = (query !== '');

        // If there are selected cabinets to show, return a list of those cabinets
        if (listItems.length !== 0) {
            return (
                <>
                    {/* Show a list of all of the remaining un-added cabinets (filtered by query) */}
                    { (listItems.length > 0) &&
                        listItems.map((cabinet: Cabinet) => {
                            return (
                                <li
                                    key={ `room-cabinet-list-item-${cabinet.cabinetId}` }
                                    id={ `arcadeRoomCabinet-${cabinet.cabinetId }` }
                                    style={{ paddingTop: 10}}
                                >
                                    <div
                                        className={ `${CabinetSelector.CLASS_LIST_ITEM} ${CabinetSelector.CLASS_LIST_ITEM_AVAILABLE}` }
                                    >
                                        <Button 
                                            type="primary"
                                            className='menu-item' 
                                            onClick={ () => this.handleCabinetAdd(cabinet) }
                                            icon={ CabinetSelector.ICON_ADD_CABINET }
                                        />
                                            <div className={'text-overflow-ellipsis fill'} style={{display: 'inline'}}>
                                                {cabinet.name}
                                            </div>
                                    </div>
                                </li>
                            );
                        })
                    }
                </>
            );
        }
        // If no cabinets are being shown, show the no results or no cabinets message
        else {
            return ( 
                <li style={{ paddingTop: 10}}>
                    <span>
                        {filtering ? noResultsText : noAvailableCabinets}
                    </span>
                </li>
            );
        }
    };

    /**
     * Removes the provided cabinet from the room.
     * @param entry - The details of the cabinet to remove from the room. 
     */
    private handleCabinetRemove = (entry: CabinetEntry | null) => {
        if (entry) {
            const { roomActions: { removeCabinetFromRoom } } = this.props;
            removeCabinetFromRoom(entry.cabinetId);
        }
    };

    /**
     * Adds the provided cabinet to the room.
     * @param cabinet - The cabinet to add to the room.
     */
    private handleCabinetAdd = (cabinet: Cabinet | null) => {
        if (cabinet) {
            this.props.roomActions.addCabinetToRoom(cabinet);
            this.props.roomActions.setReorderedCabinets([...this.props.room.cabinets, cabinet]);
        }
    };

    /**
     * This method is called every time the filter field is changed.
     */
    private handleQueryChange = async event => {
        const { value } = event.target;
        this.setState({ query: value });
    };

    /**
     * Returns true or false depending on if a cabinet entry fits the filter
     * query. The comparison between the cabinet and the query is handled by
     * checking the search query and the name of the cabinet to see if they 
     * match.
     * @param query - The search query.
     * @param entry - The cabinet to be checking.
     * @param exactMatch - If the filter should be using an exact match or not. 
     */
    private acceptedByQuery = (query: string, entry: CabinetEntry, exactMatch = false) => {
        // Clean the strings for comparison
        const sanitizedCabinetName = entry.name.toLowerCase();
        const sanitizedQuery = query.toLowerCase();

        return (exactMatch)
            ? sanitizedCabinetName === sanitizedQuery // Check if query and cabinet name are directly equivalent
            : sanitizedCabinetName.indexOf(sanitizedQuery) >= 0; // OR check if query is substring of cabinet name
    };
}
